import { useEffect, useState } from 'react';
import { Button, Input, Modal, Textarea, type SelectOption } from '../../ui';
import { MultiPicker } from '../common/MultiPicker';
import {
  blankServiceDraft,
  draftFromService,
  listToInput,
  parseList,
  validateServiceDraft,
} from '../../features/services/serviceModel';
import type { Service, ServiceDraft } from '../../features/services/types';
import type { GoalSummary } from '../../features/goals/types';
import type { WorkstreamSummary } from '../../features/workstreams/types';
import type { TeamDocumentSummary } from '../../features/documents/types';
import type { DecisionSummary } from '../../features/decisions/types';

export interface ServiceModalProps {
  open: boolean;
  onClose: () => void;
  defaultPoc: string;
  initialService?: Service;
  goals: GoalSummary[];
  workstreams: WorkstreamSummary[];
  documents: TeamDocumentSummary[];
  decisions: DecisionSummary[];
  goalTitles: Map<string, string>;
  workstreamTitles: Map<string, string>;
  documentTitles: Map<string, string>;
  decisionTitles: Map<string, string>;
  onSubmit: (draft: ServiceDraft) => Promise<unknown>;
}

/** Register / edit a Service directory entry. */
export function ServiceModal({
  open,
  onClose,
  defaultPoc,
  initialService,
  goals,
  workstreams,
  documents,
  decisions,
  goalTitles,
  workstreamTitles,
  documentTitles,
  decisionTitles,
  onSubmit,
}: ServiceModalProps) {
  const [draft, setDraft] = useState<ServiceDraft>(blankServiceDraft());
  const [reposText, setReposText] = useState('');
  const [depsText, setDepsText] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const base = initialService ? draftFromService(initialService) : blankServiceDraft(defaultPoc);
    setDraft(base);
    setReposText(listToInput(base.repositories));
    setDepsText(listToInput(base.knownDependencies));
    setProblems([]);
  }, [open, initialService, defaultPoc]);

  const set = <K extends keyof ServiceDraft>(key: K, value: ServiceDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const goalOptions: SelectOption[] = goals.map((g) => ({ value: g.id, label: g.title || g.id }));
  const workstreamOptions: SelectOption[] = workstreams.map((w) => ({ value: w.id, label: w.title || w.id }));
  const docOptions: SelectOption[] = documents.map((d) => ({ value: d.id, label: d.title || d.id }));
  const decisionOptions: SelectOption[] = decisions.map((d) => ({ value: d.id, label: d.decision || d.id }));

  const buildDraft = (): ServiceDraft => ({
    ...draft,
    name: draft.name.trim(),
    purpose: draft.purpose.trim(),
    owningTeam: draft.owningTeam.trim(),
    primaryPoc: draft.primaryPoc.trim(),
    secondaryPoc: (draft.secondaryPoc ?? '').trim() || undefined,
    repositories: parseList(reposText),
    knownDependencies: parseList(depsText),
  });

  const submit = async () => {
    const next = buildDraft();
    const found = validateServiceDraft(next);
    if (found.length > 0) {
      setProblems(found);
      return;
    }
    setSaving(true);
    try {
      await onSubmit(next);
      onClose();
    } catch (e) {
      setProblems([e instanceof Error ? e.message : 'Failed to save service.']);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !saving && onClose()}
      size="xl"
      title={initialService ? 'Edit service' : 'Register service'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button loading={saving} onClick={submit}>
            {initialService ? 'Save changes' : 'Register service'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {problems.length > 0 && (
          <ul className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {problems.map((p) => (
              <li key={p}>• {p}</li>
            ))}
          </ul>
        )}

        <Input
          label="Name (required)"
          value={draft.name}
          onChange={(e) => set('name', e.target.value)}
        />
        <Textarea
          label="Purpose (required)"
          hint="What is this system for?"
          rows={2}
          value={draft.purpose}
          onChange={(e) => set('purpose', e.target.value)}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input
            label="Owning team"
            value={draft.owningTeam}
            onChange={(e) => set('owningTeam', e.target.value)}
          />
          <Input
            label="Primary POC (required)"
            value={draft.primaryPoc}
            onChange={(e) => set('primaryPoc', e.target.value)}
          />
          <Input
            label="Secondary POC"
            placeholder="Optional"
            value={draft.secondaryPoc ?? ''}
            onChange={(e) => set('secondaryPoc', e.target.value || undefined)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Repositories"
            hint="Comma-separated names / links"
            value={reposText}
            onChange={(e) => setReposText(e.target.value)}
          />
          <Input
            label="Known dependencies"
            hint="Comma-separated"
            value={depsText}
            onChange={(e) => setDepsText(e.target.value)}
          />
        </div>

        <MultiPicker
          label="Current goals"
          options={goalOptions}
          value={draft.currentGoalIds}
          onChange={(ids) => set('currentGoalIds', ids)}
          labelFor={(id) => goalTitles.get(id) ?? id}
        />
        <MultiPicker
          label="Active workstreams"
          options={workstreamOptions}
          value={draft.activeWorkstreamIds}
          onChange={(ids) => set('activeWorkstreamIds', ids)}
          labelFor={(id) => workstreamTitles.get(id) ?? id}
        />
        <MultiPicker
          label="Documents"
          options={docOptions}
          value={draft.documentIds}
          onChange={(ids) => set('documentIds', ids)}
          labelFor={(id) => documentTitles.get(id) ?? id}
        />
        <MultiPicker
          label="Recent decisions"
          options={decisionOptions}
          value={draft.recentDecisionIds}
          onChange={(ids) => set('recentDecisionIds', ids)}
          labelFor={(id) => decisionTitles.get(id) ?? id}
        />
      </div>
    </Modal>
  );
}

export default ServiceModal;
